import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Space from 'components/generic/Space.component';
import { DOWNLOAD_EXCEL } from 'constants/csv.const';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import { exportCSV } from 'utils/converters/csv.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';

import CN from './CountOfServicesTable.module.scss';

const SERVICE_LABEL = 'Service';

const CountOfServicesTable = ({ appointmentsReport, startDate, endDate }) => {
  const { isDarkMode } = useTheme();

  const stylistGroupObject = appointmentsReport.lastRequestResponse.stylist_group || {};
  const stylistGroupsArray = Object.values(stylistGroupObject);

  const extractServicesData = (stylist) => {
    const servicesData = stylist.services || {};
    const servicesArray = Object.values(servicesData);
    const serviceCounts = servicesArray.reduce((counts, service) => {
      counts[service.name] = service.count || 0;
      return counts;
    }, {});
    return serviceCounts;
  };

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>{SERVICE_LABEL}</th>
      {stylistGroupsArray.map((stylist, index) => (
        <th key={createNodeKey({ index, tag: stylist.name })}>
          {truncateString({ value: stylist.name, max: 24, suffix: '...' })}
        </th>
      ))}
      <th className={CN.lastCell}></th>
    </tr>
  );

  // rows with service count for each stylist
  const tableRows = Object.keys(stylistGroupObject).reduce((rows, stylistKey) => {
    const stylist = stylistGroupObject[stylistKey];
    const servicesCounts = extractServicesData(stylist);

    Object.keys(servicesCounts).forEach((serviceName) => {
      const serviceCount = servicesCounts[serviceName];
      const existingRow = rows.find((row) => row.serviceName === serviceName);

      if (!existingRow) {
        const newRow = { serviceName, counts: { [stylist.name]: serviceCount } };
        rows.push(newRow);
      } else {
        existingRow.counts[stylist.name] = serviceCount;
      }
    });

    return rows;
  }, []);

  const tableBody = tableRows.map((row, rowIndex) => (
    <tr key={createNodeKey({ index: rowIndex, tag: row.serviceName })}>
      <td className={CN.firstCell}>{row.serviceName}</td>
      {stylistGroupsArray.map((stylist, stylistIndex) => (
        <td key={createNodeKey({ index: stylistIndex, tag: stylist.name })}>
          {truncateString({ value: row.counts[stylist.name] || '', max: 24, suffix: '...' })}
        </td>
      ))}
      <td className={CN.lastCell}></td>
    </tr>
  ));

  const handleExportCsv = useCallback(() => {
    const headers = [SERVICE_LABEL, ...stylistGroupsArray.map((stylist) => stylist.name)];

    const csvData = asArray(tableRows).map((row) =>
      headers.reduce((acc, header) => {
        if (header === SERVICE_LABEL) {
          acc[header] = row.serviceName || '';
        } else {
          acc[header] = row.counts[header] || '';
        }

        return acc;
      }, {}),
    );

    exportCSV({ data: csvData, fileName: 'Count of services' });
  }, [stylistGroupsArray, tableRows]);

  const hasTableData = fromAnyToBool(tableRows.length);

  return (
    <div
      data-bem="CountOfServicesTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>Count of services</div>
        <Button onClick={handleExportCsv} disabled={!hasTableData} variant="primaryForce" width="small" height="xs">
          <span className={CN.buttonFont}>{DOWNLOAD_EXCEL}</span>
        </Button>
      </div>

      <div className={CN.timeContainer}>
        <div>
          <Space>From</Space>
          <span className={CN.bold}>
            <Space>{startDate}</Space>
          </span>
        </div>
        <div>
          <Space>to</Space>
          <span className={CN.bold}>{endDate}</span>
        </div>
      </div>

      <div className={CN.scrollContainer}>
        <table className={CN.table} id="CountOfServicesTable">
          <thead>{tableHeader}</thead>
          <tbody>{tableBody}</tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointmentsReport: state?.reports?.appointmentsReport,
});

const CountOfServicesTableHoc = connect(mapStateToProps)(CountOfServicesTable);

export default CountOfServicesTableHoc;
